import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../auth/api';
import { useAuth } from '../../auth/AuthContext';
import SkyDecor from '../../components/SkyDecor';
import './ExerciseHub.css';

// --- Frontend-only gamification helpers (no backend fields exist yet) ---
// Deterministic + stable per child so values don't jump between renders.
const MASCOTS = ['🦁', '🐼', '🦊', '🐯', '🐵', '🐸', '🦄', '🐶', '🐨', '🐰'];
// Favorite-character mascot stays deterministic (no backend field for it).
const charFor = (child) => MASCOTS[(child?.id ?? 0) % MASCOTS.length];

// Shown while real stats are still loading or a child has no activity yet.
const EMPTY_STATS = { stars: 0, level: 1, level_title: 'Starter', streak: 0, stars_into_level: 0, stars_to_next_level: 50 };

// Activity cards — routes/labels preserved from the original hub.
const ACTIVITIES = [
  {
    key: 'articulation',
    route: '/child/articulation',
    icon: '🗣️',
    title: 'Speaking Adventure',
    desc: 'Practice words and sounds',
    accent: 'blue',
  },
  {
    key: 'auditory',
    route: '/child/auditory',
    icon: '👂',
    title: 'Listening Adventure',
    desc: 'Hear and match sounds',
    accent: 'purple',
  },
  {
    key: 'buddy',
    route: '/child/buddy',
    icon: '🤖',
    title: 'Buddy Chat',
    desc: 'Talk with your AI friend',
    accent: 'mint',
  },
  {
    key: 'oral',
    route: '/child/oral-motor',
    icon: '😊',
    title: 'Mouth Gym',
    desc: 'Practice mouth movements',
    accent: 'orange',
  },
];

const ExerciseHub = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [articulationAssignments, setArticulationAssignments] = useState([]);
  const [auditoryAssignments, setAuditoryAssignments] = useState([]);
  const [statsMap, setStatsMap] = useState({}); // { [child_id]: stats } — real gamification data

  useEffect(() => {
    fetchChildren();
    fetchStats();
  }, []);

  const fetchChildren = async () => {
    try {
      const response = await api.get('/children/');
      setChildren(response.data);
    } catch (error) {
      console.error('Error fetching children:', error);
    }
  };

  // Real stars/level/streak for every child in one call (avoids N+1).
  const fetchStats = async () => {
    try {
      const response = await api.get('/children/stats');
      const map = {};
      (response.data.stats || []).forEach(s => { map[s.child_id] = s; });
      setStatsMap(map);
    } catch (error) {
      console.error('Error fetching child stats:', error);
    }
  };

  // Stats for a child, falling back to zeros while loading / no activity yet.
  const getStats = (child) => statsMap[child?.id] || EMPTY_STATS;

  const handleChildSelect = async (child) => {
    setSelectedChild(child);
    try {
      const [artResp, audResp] = await Promise.all([
        api.get(`/assignments/${child.id}`),
        api.get(`/auditory/assignments/${child.id}`),
      ]);
      setArticulationAssignments(artResp.data);
      setAuditoryAssignments(audResp.data);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  const go = (activity) =>
    navigate(activity.route, {
      state: { childId: selectedChild.id, childName: selectedChild.name },
    });

  // Reward badge text per activity card.
  const badgeFor = (key) => {
    if (key === 'articulation')
      return articulationAssignments.length > 0
        ? `${articulationAssignments.length} missions`
        : 'New!';
    if (key === 'auditory')
      return auditoryAssignments.length > 0
        ? `${auditoryAssignments.length} missions`
        : 'New!';
    if (key === 'buddy') return '🎙️ Voice Chat';
    return '📷 Camera Fun';
  };

  // ---------------------------------------------------------------- Screen 1
  if (!selectedChild) {
    return (
      <div className="hub-container">
        <SkyDecor />
        <div className="hub-header">
          <div className="hub-logo">🎯 Speech Buddy</div>
          <button onClick={logout} className="hub-icon-btn" title="Logout">⎋</button>
        </div>

        <div className="hub-select">
          <h1 className="hub-title">Who is playing today?</h1>
          <div className="hub-child-grid">
            {children.map((child, i) => {
              const s = getStats(child);
              return (
                <button
                  key={child.id}
                  className={`hub-child-card accent-${i % 5}`}
                  onClick={() => handleChildSelect(child)}
                >
                  <div className="hub-child-avatar">{charFor(child)}</div>
                  <div className="hub-child-name">{child.name}</div>
                  <div className="hub-child-fav">{charFor(child)} Buddy</div>
                  <div className="hub-child-stars">⭐ {s.stars} Stars</div>
                  <div className="hub-child-level">🏆 {s.level_title} · Level {s.level}</div>
                </button>
              );
            })}
            {children.length === 0 && (
              <p className="hub-empty">No players yet — ask your therapist to add you! 🌈</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------- Screen 2
  const s = getStats(selectedChild);
  const dailyDone = Math.min(articulationAssignments.length + auditoryAssignments.length, 3);

  return (
    <div className="hub-container">
      <SkyDecor />
      <div className="hub-header">
        <button className="hub-icon-btn" onClick={() => setSelectedChild(null)} title="Back">←</button>
        <div className="hub-greeting">Hi {selectedChild.name}! 🌟</div>
        <button onClick={logout} className="hub-icon-btn" title="Logout">⎋</button>
      </div>

      <div className="hub-content">
        {/* Mascot + speech bubble */}
        <section className="hub-mascot-row">
          <div className="hub-mascot">{charFor(selectedChild)}</div>
          <div className="hub-speech">Ready for today's speech adventure?</div>
        </section>

        {/* Stat chips */}
        <section className="hub-stats">
          <div className="hub-stat chip-orange"><span>⭐</span><strong>{s.stars}</strong><small>Stars</small></div>
          <div className="hub-stat chip-pink"><span>🔥</span><strong>{s.streak}</strong><small>Day Streak</small></div>
          <div className="hub-stat chip-purple"><span>🏆</span><strong>Lv {s.level}</strong><small>{s.level_title}</small></div>
        </section>

        {/* Rewards (visual placeholders) */}
        <section className="hub-rewards">
          <div className="hub-reward-head">
            <span>Today's Goal</span>
            <span className="hub-goal-count">{dailyDone}/3 adventures</span>
          </div>
          <div className="hub-goal-bar">
            <div className="hub-goal-fill" style={{ width: `${(dailyDone / 3) * 100}%` }} />
          </div>
          <div className="hub-trophy-shelf">
            {['🥇', '🎖️', '🏅', '🎀', '🌈'].map((t, i) => (
              <span key={i} className={`hub-trophy ${i < 2 ? 'earned' : 'locked'}`}>{t}</span>
            ))}
            <span className="hub-shelf-label">Trophy Shelf</span>
          </div>
          <div className="hub-path">
            {[0, 1, 2, 3, 4].map((n) => (
              <span key={n} className={`hub-step ${n <= dailyDone ? 'done' : ''}`}>{n <= dailyDone ? '⭐' : '•'}</span>
            ))}
          </div>
        </section>

        {/* Activity cards */}
        <section className="hub-activity-grid">
          {ACTIVITIES.map((a) => (
            <div
              key={a.key}
              className={`hub-activity-card accent-${a.accent}`}
              onClick={() => go(a)}
            >
              <div className="hub-activity-illus">{a.icon}</div>
              <div className="hub-activity-body">
                <h3 className="hub-activity-title">{a.title}</h3>
                <p className="hub-activity-desc">{a.desc}</p>
                <span className="hub-activity-badge">{badgeFor(a.key)}</span>
              </div>
              <button className="hub-start-btn" onClick={(e) => { e.stopPropagation(); go(a); }}>
                Start →
              </button>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
};

export default ExerciseHub;
